import { useState, useEffect } from 'react';
import useTranslations from '~/components/translation/use-translations';
import insertValue from '~/components/casino-shared/tournaments/utils/insert-value';

const TEXT_KEYS = [
	'TOURNAMENTS_TIME_ACTIVE',
	'TOURNAMENTS_TIME_ENDED',
	'TOURNAMENTS_TIME_ENDS_ON',
	'TOURNAMENTS_TIME_ENDS_AT',
	'TOURNAMENTS_TIME_STARTS_ON',
	'TOURNAMENTS_TIME_STARTS_AT'
];

const getTimeText = ({
	startDate,
	endDate,
	hasStarted,
	hasEnded,
	isEndingToday,
	translations,
	isLoadingTranslations,
	skipActiveState
}: {
	startDate: string;
	endDate: string;
	hasStarted: boolean;
	hasEnded: boolean;
	isEndingToday: boolean;
	translations: Record<string, string>;
	isLoadingTranslations: boolean;
	skipActiveState: boolean;
}) => {
	if (isLoadingTranslations) {
		return '';
	}

	if (hasEnded) {
		return translations.TOURNAMENTS_TIME_ENDED;
	}

	const startTime = new Date(startDate);
	const endTime = new Date(endDate);

	if (hasStarted) {
		if (isEndingToday) {
			const endTimeString = endTime.toLocaleTimeString(undefined, {
				hour: 'numeric',
				minute: '2-digit'
			});

			return insertValue(translations.TOURNAMENTS_TIME_ENDS_AT, endTimeString);
		}

		if (skipActiveState) {
			const endDateString = endTime.toLocaleDateString(undefined, {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit'
			});

			return insertValue(translations.TOURNAMENTS_TIME_ENDS_ON, endDateString);
		}

		return translations.TOURNAMENTS_TIME_ACTIVE;
	}

	const timeUntilStart = startTime.getTime() - Date.now();
	const daysUntilStart = timeUntilStart / (24 * 60 * 60 * 1000);

	if (daysUntilStart > 1) {
		const startDateString = startTime.toLocaleDateString(undefined, {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		});

		return insertValue(translations.TOURNAMENTS_TIME_STARTS_ON, startDateString);
	}

	const startTimeString = startTime.toLocaleTimeString(undefined, {
		hour: 'numeric',
		minute: '2-digit'
	});

	return insertValue(translations.TOURNAMENTS_TIME_STARTS_AT, startTimeString);
};

const useTimeTicker = (startDate: string, endDate: string, skipActiveState = false) => {
	const [hasStarted, setHasStarted] = useState(false);
	const [hasEnded, setHasEnded] = useState(false);
	const [isEndingToday, setIsEndingToday] = useState(false);
	const [timeText, setTimeText] = useState('');
	const { translations, isLoadingTranslations } = useTranslations(TEXT_KEYS);

	useEffect(() => {
		const startTime = new Date(startDate).getTime();
		const endTime = new Date(endDate).getTime();
		let timeLeft = 0;
		let requestId = 0;
		let isMounted = true;

		const tick = () => {
			const now = Date.now();
			const started = now > startTime;
			const ended = now > endTime;

			timeLeft = (started ? endTime : startTime) - now;

			const endsTodayCheck = started && !ended && timeLeft / (24 * 60 * 60 * 1000) <= 1;

			setHasStarted(started);
			setHasEnded(ended);
			setIsEndingToday(endsTodayCheck);

			setTimeText(
				getTimeText({
					startDate,
					endDate,
					hasStarted: started,
					hasEnded: ended,
					isEndingToday: endsTodayCheck,
					translations,
					isLoadingTranslations,
					skipActiveState
				})
			);

			if (!ended && timeLeft > 0) {
				setTimeout(() => {
					if (isMounted) {
						requestId = window.requestAnimationFrame(tick);
					}
				}, 1000);
			}
		};

		requestId = window.requestAnimationFrame(tick);

		return () => {
			window.cancelAnimationFrame(requestId);
			isMounted = false;
		};
	}, [startDate, endDate, translations, isLoadingTranslations, skipActiveState]);

	return {
		hasStarted,
		hasEnded,
		isEndingToday,
		timeText
	};
};

export default useTimeTicker;
